if (!customElements.get('naire-product-options')) {
  customElements.define('naire-product-options', class NaireProductOptions extends HTMLElement {
    connectedCallback() {
      document.documentElement.classList.add('has-naire-product');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return requestAnimationFrame(() => this.connectedCallback());
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.previewProduct = this.dataset.previewProduct === 'true' || !this.form.querySelector('[name="id"]')?.value;
      this.previewMismatch = this.dataset.previewMismatch === 'true';
      if (this.previewProduct) document.documentElement.classList.add('has-naire-preview-product');
      this.fileInput = this.querySelector('[data-naire-file]');
      this.quantity = this.querySelector('[data-naire-quantity]');
      this.agreement = this.querySelector('[data-naire-agreement]');
      this.submit = this.form.querySelector('[name="add"]');
      this.modal = this.querySelector('[data-naire-modal]');
      this.productInfo = this.closest('product-info');
      this.variantSelects = this.closest('.product__info-container')?.querySelector('variant-selects');
      this.bindEvents();
      this.updateTemplateLink();
      this.updateState();
    }

    bindEvents() {
      this.addEventListener('change', () => this.updateState());
      this.productInfo?.addEventListener('click', (event) => {
        if (!this.isPreviewProduct()) return;
        const label = event.target?.closest?.('.naire-variant label[for]');
        const input = label ? document.getElementById(label.htmlFor) : null;
        if (!input || input.classList.contains('disabled')) return;
        this.previewProduct = true;
        document.documentElement.classList.add('has-naire-preview-product');
        event.preventDefault();
        event.stopPropagation();
        input.checked = true;
        const output = input.closest('.naire-variant')?.querySelector('[data-selected-value]');
        if (output) output.textContent = input.value;
        const variant = this.variantForCurrentOptions();
        this.selectVariantLocally(variant);
        this.updateTemplateLink(variant);
        this.updateState();
      }, true);
      this.productInfo?.addEventListener('change', (event) => {
        const group = event.target?.closest?.('.naire-variant');
        if (!group || !this.productInfo.contains(group)) return;
        if (!this.isPreviewProduct()) return;
        const selected = event.target?.value;
        const output = group?.querySelector('[data-selected-value]');
        if (output && selected) output.textContent = selected;
        if (!group.classList.contains('naire-variant--color')) {
          this.updateTemplateLink();
          return;
        }
        event.stopPropagation();
        const variant = this.variantForCurrentOptions();
        this.selectVariantLocally(variant);
        this.updateState();
      }, true);
      this.quantity?.addEventListener('input', () => this.updateState());
      this.form.addEventListener('submit', (event) => this.validate(event), true);
      this.querySelector('[data-naire-notice-open]')?.addEventListener('click', () => this.modal?.showModal());
      this.querySelector('[data-naire-notice-confirm]')?.addEventListener('click', () => {
        this.dataset.noticeConfirmed = 'true';
        this.agreement.disabled = false;
        this.agreement.checked = true;
        this.modal?.close();
        this.updateState();
      });
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.fileInput?.addEventListener('change', () => this.handleFile());
      this.querySelector('[data-naire-remove-file]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.fileInput.value = '';
        this.handleFile();
      });
      const dropzone = this.querySelector('[data-naire-dropzone]');
      ['dragenter', 'dragover'].forEach((name) => dropzone?.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.add('is-dragover'); }));
      ['dragleave', 'drop'].forEach((name) => dropzone?.addEventListener(name, (event) => { event.preventDefault(); dropzone.classList.remove('is-dragover'); }));
      dropzone?.addEventListener('drop', (event) => {
        if (!event.dataTransfer?.files?.length) return;
        this.fileInput.files = event.dataTransfer.files;
        this.handleFile();
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;
          this.updateTemplateLink(data.variant);
          queueMicrotask(() => this.updateState());
        });
      }
      this.form.querySelector('[name="id"]')?.addEventListener('change', () => { this.updateTemplateLink(); this.updateState(); });
    }

    disconnectedCallback() { this.unsubscribe?.(); }

    isPreviewProduct() {
      return this.previewProduct || !this.form?.querySelector('[name="id"]')?.value;
    }

    isRepeatChangingPosition() {
      return this.dataset.repeat === 'true' && this.querySelector('[name="properties[名入れ位置の指定]"]:checked')?.value === '位置を変更する';
    }

    handleFile() {
      const file = this.fileInput?.files?.[0];
      const name = this.querySelector('[data-naire-file-name]');
      const empty = this.querySelector('[data-naire-file-empty]');
      const error = this.querySelector('[data-error-for="file"]');
      const max = Number(this.dataset.maxFileSize || 20971520);
      let message = '';
      if (file && !file.name.toLowerCase().endsWith('.ai')) message = 'Adobe Illustrator形式（.ai）のファイルを選択してください。';
      if (file && file.size > max) message = `ファイル容量は${Math.round(max / 1048576)}MB以下にしてください。`;
      if (message) this.fileInput.value = '';
      const validFile = this.fileInput?.files?.[0];
      if (name) name.textContent = validFile?.name || '';
      if (empty) empty.hidden = Boolean(validFile);
      this.querySelector('[data-naire-file-present]')?.toggleAttribute('hidden', !validFile);
      if (error) { error.textContent = message || 'Illustrator（.ai）ファイルを選択してください。'; error.hidden = !message; }
      this.updateState();
    }

    selectedVariant() {
      const id = Number(this.form.querySelector('[name="id"]')?.value);
      return this.variants().find((variant) => variant.id === id) || null;
    }

    variants() {
      try { return JSON.parse(this.querySelector('[data-naire-variants]')?.textContent || '[]'); } catch { return []; }
    }

    currentVariantSelects() {
      return this.productInfo?.querySelector('variant-selects') || this.variantSelects;
    }

    variantForCurrentOptions() {
      const values = [...(this.currentVariantSelects()?.querySelectorAll('fieldset') || [])]
        .map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
      if (!values.length || values.some((value) => !value)) return null;
      return this.variants().find((variant) => variant.options?.every((option, index) => option === values[index])) || null;
    }

    selectVariantLocally(variant) {
      const input = this.form.querySelector('[name="id"]');
      if (!input) return;
      input.value = variant?.id || '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateTemplateLink(variant = this.selectedVariant()) {
      const link = this.querySelector('[data-naire-template]');
      if (!link) return;
      let href = link.dataset.templateDefault || '';
      let filename = link.dataset.templateFileDefault || 'naire-template-utsuwa.zip';
      let label = this.dataset.family === 'utsuwa' ? 'UTSUWA用テンプレートをダウンロード' : 'テンプレートをダウンロード';
      if (this.dataset.family === 'ori') {
        const index = Number(this.dataset.sizeOptionIndex || 0);
        const selectedOptions = [...(this.currentVariantSelects()?.querySelectorAll('fieldset') || [])]
          .map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
        const size = variant?.options?.[index] || selectedOptions[index] || '';
        const key = size.includes('1.5') || size.includes('1.5合') ? '15' : size.includes('2') ? '2' : size.includes('1') ? '1' : '';
        if (!key) return;
        href = key ? link.dataset[`template-${key}`] : '';
        if (key) {
          label = `Ori ${key === '15' ? '1.5' : key}合用テンプレートをダウンロード`;
          filename = link.dataset[`templateFile-${key}`];
        }
      }
      link.href = href || '#';
      link.download = filename;
      link.setAttribute('aria-disabled', href ? 'false' : 'true');
      link.querySelector('[data-naire-template-label]').textContent = href ? label : 'テンプレート準備中';
    }

    state() {
      const foil = this.querySelector('[name="properties[箔の色]"]:checked');
      const mode = this.querySelector('[name="properties[名入れ位置の指定]"]:checked');
      const position = this.querySelector('[name="properties[名入れ位置]"]:checked');
      const file = this.fileInput?.files?.[0];
      const quantity = Number(this.quantity?.value || 0);
      const repeat = this.dataset.repeat === 'true';
      const changing = this.isRepeatChangingPosition();
      const variant = this.selectedVariant();
      return {
        variant: Boolean(variant?.available), foil: Boolean(foil), mode: !repeat || Boolean(mode),
        position: repeat ? (!changing || Boolean(position)) : Boolean(position),
        file: repeat ? (!changing || Boolean(file)) : Boolean(file),
        quantity: quantity >= Number(this.dataset.minimum || 5), agreement: Boolean(this.agreement?.checked),
        plate: repeat || this.dataset.plateFeeReady === 'true', changing
      };
    }

    updateState() {
      const state = this.state();
      this.querySelector('[data-naire-change-fields]')?.toggleAttribute('hidden', !state.changing);
      this.querySelector('[data-naire-upload-group]')?.toggleAttribute('hidden', this.dataset.repeat === 'true' && !state.changing);
      if (this.dataset.repeat === 'true' && !state.changing) {
        this.querySelectorAll('[name="properties[名入れ位置]"]').forEach((input) => { input.checked = false; });
        if (this.fileInput?.value) {
          this.fileInput.value = '';
          this.handleFile();
          return;
        }
      }
      ['foil', 'position', 'file', 'quantity', 'agreement', 'plate'].forEach((key) => {
        if (state[key]) this.querySelector(`[data-error-for="${key}"]`)?.setAttribute('hidden', '');
      });
      this.querySelectorAll('[data-naire-property-group]').forEach((group) => {
        const property = group.dataset.nairePropertyGroup;
        const selected = group.querySelector(`[name="properties[${property}]"]:checked`);
        const output = group.querySelector('[data-naire-selected]');
        if (output) output.textContent = selected?.value || '未選択';
      });
      const ready = Object.entries(state).filter(([key]) => key !== 'changing').every(([, value]) => value);
      if (this.submit) this.submit.disabled = this.previewMismatch || !ready;
    }

    validate(event) {
      const state = this.state();
      const errors = Object.entries(state).filter(([key, value]) => key !== 'changing' && !value).map(([key]) => key);
      ['foil', 'position', 'file', 'quantity', 'agreement', 'plate'].forEach((key) => {
        const element = this.querySelector(`[data-error-for="${key}"]`);
        if (element) element.hidden = !errors.includes(key);
      });
      const alert = this.querySelector('[data-naire-form-error]');
      if (alert) alert.hidden = errors.length === 0;
      if (!errors.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      alert?.focus();
      this.querySelector(`[data-error-for="${errors[0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
